import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { NotFound } from '@client/components/NotFound';
import { useAreFeatureFlagsSettled, useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { selectUserOrgs, useAuthStore } from '@client/stores/AuthStore';
import { getAuth0Client, loginWithRedirect } from '@client/utils/auth/auth0Client';
import {
    type ClientInfo,
    classifyResource,
    consentGrantedScope,
    ipRestrictionSatisfied,
    operatorConsentFields,
    resolveAdminConsent,
    resolveConsentAccess,
    resolveConsentOrg,
} from '@client/utils/auth/oauth-consent';
import {
    PERSONAL_ACCESS_TOKEN_REASON_MAX_LENGTH,
    PERSONAL_ACCESS_TOKEN_REASON_MIN_LENGTH,
    PERSONAL_ACCESS_TOKEN_SCOPE_FULL,
    PERSONAL_ACCESS_TOKEN_SCOPE_PARAM_MAX_LENGTH,
    PERSONAL_ACCESS_TOKEN_SCOPE_READ,
    type PersonalAccessTokenScope,
    parsePersonalAccessTokenScopeParam,
} from '@shared/constants/system/personal-access-token';
import { redirectUriIsRegistered } from '@shared/utils/core/oauth-redirect-uri';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Alert, Checkbox, Form, Input, Radio, Select } from 'antd';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

/**
 * /oauth/authorize — the user-facing OAuth consent page.
 *
 * Why a SPA route (not a server-rendered HTML page): the only place to check
 * whether the user is currently logged into Curium is the browser, because
 * the Auth0 SPA SDK stores tokens in localStorage (see
 * client/src/utils/auth/auth0Client.ts:13 — `cacheLocation: 'localstorage'`).
 *
 * Flow:
 *   1. Validate the OAuth query params client-side; render an inline error page
 *      if `redirect_uri` is malformed (never redirect to a URL we can't trust).
 *   2. Call `getAuth0Client().isAuthenticated()`. If false → loginWithRedirect
 *      with `appState.redirectTo = current URL` so /callback comes back here.
 *   3. Call `getTokenSilently()` to get a fresh Auth0 JWT (silent when the
 *      tenant session is still valid — no user-visible login screen).
 *   4. Call `GET /oauth/client-info?clientId=...` for sanitised metadata
 *      (clientName, validated redirectUris, same-origin logoUrl).
 *   5. Render the consent form: org picker + access (read only by default; full
 *      access only when the client asked for it and the user picks it) + expiry +
 *      token name + exact redirect_uri display + Authorise/Deny. For the operator `/mcp-admin`
 *      resource the form also captures a reason, a current-address opt-in (resolved
 *      by the server) and a Claude connector-network opt-in, offered when client-info's
 *      `connectorIpAllowlist` is non-empty. `adminGrantRules` caps expiry and, in
 *      production, requires a reason and at least one network restriction.
 *   6. On Authorise: `POST /oauth/consent` with the JWT → server returns
 *      `{ redirectTo }` → window.location.assign(redirectTo) bounces the
 *      browser to the MCP client's loopback callback with the auth code.
 *   7. On Deny: window.location.assign(`<redirect_uri>?error=access_denied&state=...`).
 *
 * The whole flow gates on the `enable-mcp-server` feature flag — when it's off
 * we render a 404-ish "not available" state so we don't leak the endpoint's
 * existence in screenshots or status checks.
 */

// A PKCE S256 `code_challenge` is BASE64URL(SHA-256(verifier)) — strictly the base64url
// alphabet (`A–Z a–z 0–9 - _`), no padding. The unreserved verifier characters `.` and `~`
// (RFC 7636 §4.1) are NOT valid in an S256 challenge, so they are excluded here to keep
// client validation aligned with what the server accepts.
const CHALLENGE_OK = /^[A-Za-z0-9_-]{43,128}$/;

const searchSchema = z.object({
    response_type: z.literal('code'),
    client_id: z.url(),
    redirect_uri: z.string().min(1),
    code_challenge: z.string().refine((v) => CHALLENGE_OK.test(v), {
        message: 'code_challenge must be 43–128 base64url chars',
    }),
    code_challenge_method: z.literal('S256'),
    state: z.string().min(1),
    // The client's request: space-separated known scopes (`curium:read`, `curium:full`). It caps
    // what the form offers; the user's choice is what is granted.
    scope: z
        .string()
        .max(PERSONAL_ACCESS_TOKEN_SCOPE_PARAM_MAX_LENGTH)
        .refine((value) => parsePersonalAccessTokenScopeParam(value) !== null, {
            message: 'scope must name curium:read, curium:full or both',
        }),
    resource: z.url(),
});

type SearchParams = z.infer<typeof searchSchema>;

const CURIUM_LOGO_URL = 'https://curium-public.syd1.digitaloceanspaces.com/brand/curium-logo.webp';

export const Route = createFileRoute('/oauth/authorize')({
    validateSearch: zodValidator(searchSchema),
    component: OAuthAuthorize,
});

/** antd rule: the trimmed reason must meet the server's minimum (padding with spaces does not count). */
const trimmedReasonRule = {
    validator: (_rule: unknown, value: string | undefined) =>
        value === undefined || value.trim() === '' || value.trim().length >= PERSONAL_ACCESS_TOKEN_REASON_MIN_LENGTH
            ? Promise.resolve()
            : Promise.reject(
                  new Error(`Reason must be at least ${PERSONAL_ACCESS_TOKEN_REASON_MIN_LENGTH} characters`),
              ),
};

/**
 * Send the browser through Auth0 login; /callback bootstraps the AuthStore and returns to this exact URL.
 * /callback decodes the whole `redirectTo` once, so it travels encoded: a raw query would lose its
 * escapes there (`state=a%2Bb%26c` would come back as `a+b&c`, and a `redirect_uri` carrying its own
 * query would be split into separate parameters).
 */
function loginAndReturnHere() {
    return loginWithRedirect({
        appState: { redirectTo: encodeURIComponent(`${window.location.pathname}${window.location.search}`) },
    });
}

type FetchState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; info: ClientInfo }
    | { kind: 'error'; message: string };

function OAuthAuthorize() {
    const search = Route.useSearch() as SearchParams;
    const featureEnabled = useFeatureFlag('enable-mcp-server', false);
    // `useFeatureFlag` reads `false` for "not evaluated yet" as well as "off", and this route
    // sits outside `RequireAuth` / `RouteAccessGuard`, so nothing upstream waits for PostHog.
    // Its normal caller is an MCP client opening a fresh browser tab (and then the return leg
    // of the Auth0 redirect) — cold loads where `enable-mcp-server` is unknown until `/flags`
    // answers, because the server bootstrap only carries the maintenance flags. Treat the flag
    // as unknown until initialisation has settled rather than 404ing on the transient value.
    // No identity is passed: this page never runs `identifyUser()`, so init-only readiness is
    // the definitive signal here.
    const areFeatureFlagsSettled = useAreFeatureFlagsSettled();

    // Validate the redirect_uri locally first. Any redirect-bearing failure thereafter
    // MUST go back to redirect_uri (after the server re-validates), but an unparseable
    // redirect_uri can never be trusted, so we render inline.
    const redirectUriParsed = useMemo(() => {
        try {
            return new URL(search.redirect_uri);
        } catch {
            return null;
        }
    }, [search.redirect_uri]);

    if (!areFeatureFlagsSettled) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loading size="large" />
            </div>
        );
    }

    if (!featureEnabled) {
        // Render the 404 in place rather than navigating away: a navigation would be
        // irreversible, and it would also drop the OAuth query string the MCP client needs us
        // to carry through the consent flow.
        return <NotFound />;
    }

    if (!redirectUriParsed) {
        return <InlineError title="Invalid redirect URI" message="The redirect_uri parameter is not a valid URL." />;
    }

    if (classifyResource(search.resource, window.location.origin) === 'foreign') {
        // The server only consents to this deployment's own /mcp and /mcp-admin resources; say so
        // here rather than collecting a form the consent route will reject.
        return (
            <InlineError
                title="Invalid resource"
                message="The resource parameter does not point at this Curium deployment."
                code={`resource: ${search.resource}`}
            />
        );
    }

    return <ConsentFlow search={search} redirectUri={redirectUriParsed} />;
}

function ConsentFlow({ search, redirectUri }: { search: SearchParams; redirectUri: URL }) {
    const userOrgs = useAuthStore(selectUserOrgs);
    const [authChecked, setAuthChecked] = useState(false);
    const [clientInfo, setClientInfo] = useState<FetchState>({ kind: 'idle' });
    const [jwt, setJwt] = useState<string | null>(null);

    // Step 1+2: Auth0 session check, then fresh JWT.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const client = getAuth0Client();
                const authed = await client.isAuthenticated();
                if (!authed) {
                    await loginAndReturnHere();
                    return; // browser navigates away
                }
                const token = await client.getTokenSilently();
                if (cancelled) return;
                // auth0-spa-js types a missing token as `undefined`; treat it like a failed refresh.
                if (token === undefined) throw new Error('Auth0 returned no access token');
                setJwt(token);
                setAuthChecked(true);
            } catch {
                if (cancelled) return;
                // Silent refresh failed — push the user through the explicit login flow.
                await loginAndReturnHere();
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Step 3: fetch client metadata.
    useEffect(() => {
        if (!authChecked) return;
        let cancelled = false;
        setClientInfo({ kind: 'loading' });
        void (async () => {
            try {
                const url = new URL('/oauth/client-info', window.location.origin);
                url.searchParams.set('clientId', search.client_id);
                const res = await fetch(url.toString(), { credentials: 'omit' });
                if (!res.ok) {
                    const body = (await res.json().catch(() => ({}))) as { error_description?: string; error?: string };
                    const detail = body.error_description ?? body.error ?? `HTTP ${res.status}`;
                    if (!cancelled) setClientInfo({ kind: 'error', message: detail });
                    return;
                }
                const info = (await res.json()) as ClientInfo;
                // Port-flexible match for loopback redirect URIs (RFC 8252 §7.3) — native
                // clients register a portless callback and bind an ephemeral port at runtime.
                if (!redirectUriIsRegistered(search.redirect_uri, info.redirectUris)) {
                    if (!cancelled) {
                        setClientInfo({
                            kind: 'error',
                            message: 'The requested redirect URI is not registered for this client.',
                        });
                    }
                    return;
                }
                if (!cancelled) setClientInfo({ kind: 'ready', info });
            } catch (err) {
                if (!cancelled) {
                    setClientInfo({
                        kind: 'error',
                        message: err instanceof Error ? err.message : 'Failed to fetch client info',
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authChecked, search.client_id, search.redirect_uri]);

    if (!authChecked || clientInfo.kind === 'idle' || clientInfo.kind === 'loading') {
        return (
            <PageController route={Route}>
                <ConsentChrome>
                    <div className="flex flex-col items-center gap-3 py-10">
                        <Loading size="large" />
                        <p className="text-muted-foreground">Loading…</p>
                    </div>
                </ConsentChrome>
            </PageController>
        );
    }

    if (clientInfo.kind === 'error') {
        // Errors after redirect_uri has been validated MAY redirect back to the client per
        // RFC 6749 §4.1.2.1, but we've also failed our local trust checks at this point,
        // so prefer to render an inline error rather than redirect.
        return (
            <InlineError
                title="Cannot authorise this app"
                message={clientInfo.message}
                code={`client_id: ${search.client_id}`}
            />
        );
    }

    return (
        <PageController route={Route}>
            <ConsentChrome>
                <ConsentForm
                    search={search}
                    redirectUri={redirectUri}
                    clientInfo={clientInfo.info}
                    jwt={jwt!}
                    userOrgs={userOrgs}
                />
            </ConsentChrome>
        </PageController>
    );
}

function ConsentChrome({ children }: { children: React.ReactNode }) {
    return (
        <div className="min-h-screen w-full bg-muted">
            <div className="flex min-h-screen items-center justify-center px-4">
                <div className="w-full max-w-lg">
                    <div className="mb-6 flex justify-center">
                        <img src={CURIUM_LOGO_URL} alt="Curium" className="h-12 w-auto" />
                    </div>
                    <div className="rounded-lg border border-separator bg-card p-6 sm:p-8">{children}</div>
                </div>
            </div>
        </div>
    );
}

type ConsentFormValues = {
    orgId: string;
    /** The access granted. Read-only unless the user picks full access. */
    scope: PersonalAccessTokenScope;
    expiryDays: number;
    tokenName: string;
    reason?: string;
    restrictToCurrentIp?: boolean;
    allowConnectorNetwork?: boolean;
};

function ConsentForm({
    search,
    redirectUri,
    clientInfo,
    jwt,
    userOrgs,
}: {
    search: SearchParams;
    redirectUri: URL;
    clientInfo: ClientInfo;
    jwt: string;
    userOrgs: ReturnType<typeof selectUserOrgs>;
}) {
    const [form] = Form.useForm<ConsentFormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // The admin grant rules come from the server (client-info → `operatorTokenRules`), so the
    // form asks for exactly what the consent route will enforce.
    const {
        isAdminMcp,
        rules: adminRules,
        expiryOptions,
        defaultExpiryDays,
        ipRestrictionRequired,
        connectorIpAllowlist,
        connectorNetworkAvailable,
        ipRestrictionImpossible,
        initialRestrictToCurrentIp,
    } = resolveAdminConsent(search.resource, window.location.origin, clientInfo);
    // The org is pinned for an admin grant (system console only) and for a tenant grant whose
    // resource names an org (`/mcp?org=<id>`); the picker only appears when there is a choice.
    // A pinned value still travels in the form as `orgId`.
    const consentOrg = resolveConsentOrg({ isAdminMcp, resource: search.resource, userOrgs });
    // Read-only by default (CUR-2056); full access only when the client asked for it AND the user
    // picks it. The copy below follows the current choice so the user sees what they grant. A
    // server that does not issue read-only grants yet (its rollout fence) gets full access only.
    const access = resolveConsentAccess({
        isAdminMcp,
        requestedScope: search.scope,
        readOnlyAvailable: clientInfo.readOnlyScopeAvailable === true,
    });
    const watchedScope = Form.useWatch('scope', form);
    const grantsWrite =
        access.grantable &&
        consentGrantedScope(access, watchedScope ?? access.initialScope) === PERSONAL_ACCESS_TOKEN_SCOPE_FULL;
    const accessVerb = grantsWrite ? 'read and modify' : 'read';
    // An Auth0 session without a persisted Curium user (the AuthStore was never bootstrapped in
    // this browser) leaves nothing to bind the grant to. Signing in again runs /callback's
    // bootstrap and comes back here with the memberships loaded.
    const orgsNotLoaded = userOrgs.length === 0;

    const isLoopback = useMemo(() => {
        const host = redirectUri.hostname.toLowerCase();
        return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
    }, [redirectUri]);

    const isPrivateUse = redirectUri.protocol !== 'http:' && redirectUri.protocol !== 'https:';
    const isNativeHandoff = isLoopback || isPrivateUse;

    // Subtitle under the client name — the trust anchor for the app's identity. A CIMD client_id
    // is a third-party metadata URL, so its host (e.g. "claude.ai") is a domain the client proved
    // it controls. A dynamically-registered (RFC 7591) client_id is minted on our own origin and
    // carries no domain-ownership proof, so showing our host would mislead and the self-declared
    // client_name can't be trusted either. Anchor on the one verifiable fact instead — the
    // callback destination (also shown in full below). Native callbacks may have no host.
    const clientSubtitle = useMemo(() => {
        try {
            const clientUrl = new URL(search.client_id);
            if (clientUrl.origin === window.location.origin) {
                const destination =
                    redirectUri.protocol === 'http:' || redirectUri.protocol === 'https:'
                        ? redirectUri.host
                        : `${redirectUri.protocol}${redirectUri.host ? `//${redirectUri.host}` : redirectUri.pathname}`;
                return `Redirects to ${destination}`;
            }
            return clientUrl.host;
        } catch {
            return search.client_id;
        }
    }, [search.client_id, redirectUri]);

    const handleAuthorise = async (values: ConsentFormValues): Promise<void> => {
        // Nothing the client asked for can be granted here; never submit wider access instead.
        if (!access.grantable) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const res = await fetch('/oauth/consent', {
                method: 'POST',
                credentials: 'omit',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwt}`,
                },
                body: JSON.stringify({
                    clientId: search.client_id,
                    redirectUri: search.redirect_uri,
                    state: search.state,
                    codeChallenge: search.code_challenge,
                    codeChallengeMethod: search.code_challenge_method,
                    // The granted scope, never the client's request: read-only unless chosen.
                    scope: consentGrantedScope(access, values.scope),
                    resource: search.resource,
                    orgId: values.orgId,
                    expiryDays: values.expiryDays,
                    tokenName: values.tokenName.trim(),
                    ...operatorConsentFields(isAdminMcp, values),
                }),
            });
            if (!res.ok) {
                const body = (await res.json().catch(() => ({}))) as { error_description?: string; error?: string };
                setSubmitError(body.error_description ?? body.error ?? `Authorisation failed (HTTP ${res.status})`);
                setSubmitting(false);
                return;
            }
            const data = (await res.json()) as { redirectTo: string };
            // Server validated the redirect URL; client just navigates.
            window.location.assign(data.redirectTo);
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Network error');
            setSubmitting(false);
        }
    };

    const handleDeny = (): void => {
        const denyUrl = new URL(redirectUri.toString());
        denyUrl.searchParams.set('error', 'access_denied');
        denyUrl.searchParams.set('state', search.state);
        window.location.assign(denyUrl.toString());
    };

    return (
        <div>
            <div className="mb-6 flex items-start gap-4">
                {clientInfo.logoUrl ? (
                    <img
                        src={clientInfo.logoUrl}
                        alt={clientInfo.clientName}
                        className="h-12 w-12 rounded border border-separator object-contain"
                    />
                ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded border border-separator bg-muted">
                        <ShieldCheck className="h-6 w-6 text-muted-foreground" />
                    </div>
                )}
                <div className="flex-1">
                    {/* curium:allow-heading — OAuth consent screen, no app shell */}
                    <h1 className="font-bold text-foreground text-xl">{clientInfo.clientName}</h1>
                    <p className="break-all text-muted-foreground text-sm">{clientSubtitle}</p>
                </div>
            </div>

            <p className="mb-4 text-foreground">
                <span className="font-semibold">{clientInfo.clientName}</span> is requesting access to your Curium
                account.{' '}
                {isAdminMcp
                    ? 'This will let it use the system console admin MCP with your super admin roles, acting as you, until you revoke it.'
                    : `This will let it ${accessVerb} data in ${consentOrg.pinned ? consentOrg.pinnedOrgName : 'the selected organisation'}, acting as you, until you revoke it.`}
                {!isAdminMcp && !grantsWrite && ' It cannot make changes.'}
            </p>

            <div className="mb-4 rounded-lg border border-separator bg-muted p-3 text-sm">
                <div className="mb-1 font-semibold text-muted-foreground text-xs uppercase">Will redirect to</div>
                <code className="block break-all font-mono text-foreground">{redirectUri.toString()}</code>
                {isNativeHandoff && (
                    <p className="mt-2 text-muted-foreground text-xs">
                        {isPrivateUse
                            ? 'This opens a desktop app on this computer. Another installed app may claim this address.'
                            : 'This is a local loopback address used by desktop apps.'}{' '}
                        Only continue if you started this authorisation yourself.
                    </p>
                )}
            </div>

            <Form<ConsentFormValues>
                form={form}
                layout="vertical"
                onFinish={handleAuthorise}
                initialValues={{
                    orgId: consentOrg.initialOrgId,
                    scope: access.initialScope,
                    expiryDays: defaultExpiryDays,
                    tokenName: clientInfo.clientName,
                    restrictToCurrentIp: initialRestrictToCurrentIp,
                    allowConnectorNetwork: false,
                }}
            >
                {consentOrg.pinned ? (
                    <>
                        <Form.Item<ConsentFormValues> name="orgId" hidden>
                            <Input type="hidden" />
                        </Form.Item>
                        <div className="mb-4">
                            <div className="mb-1 font-semibold text-muted-foreground text-xs uppercase">
                                Organisation
                            </div>
                            <div className="text-foreground">{consentOrg.pinnedOrgName}</div>
                        </div>
                        {!consentOrg.available && !orgsNotLoaded && (
                            <Alert
                                type="error"
                                showIcon
                                className="mb-4"
                                message="Cannot authorise from here"
                                description={
                                    isAdminMcp
                                        ? 'Admin MCP grants are bound to the system console, which this account is not a member of.'
                                        : 'This MCP server URL names an organisation this account is not a member of.'
                                }
                            />
                        )}
                    </>
                ) : (
                    <Form.Item<ConsentFormValues>
                        name="orgId"
                        label="Organisation"
                        rules={[{ required: true, message: 'Choose the organisation to authorise access for' }]}
                    >
                        <Select
                            options={userOrgs.map((o) => ({ value: o.id.toString(), label: o.name }))}
                            disabled={userOrgs.length <= 1}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                )}

                {orgsNotLoaded && (
                    <Alert
                        type="warning"
                        showIcon
                        className="mb-4"
                        message="Your organisations have not loaded"
                        description="Sign in again to load the organisations you can authorise access for. You will come back to this page."
                        action={
                            <Button size="small" onClick={() => void loginAndReturnHere()}>
                                Sign in again
                            </Button>
                        }
                    />
                )}

                {!isAdminMcp &&
                    (access.choosable ? (
                        <Form.Item<ConsentFormValues> name="scope" label="Access">
                            <Radio.Group aria-label="Access" className="flex flex-col gap-2">
                                <Radio value={PERSONAL_ACCESS_TOKEN_SCOPE_READ}>
                                    <span className="font-medium text-foreground">Read only (recommended)</span>
                                    <span className="block text-muted-foreground text-sm">
                                        It can look up and report on your data. It cannot create, change or delete
                                        anything.
                                    </span>
                                </Radio>
                                <Radio value={PERSONAL_ACCESS_TOKEN_SCOPE_FULL}>
                                    <span className="font-medium text-foreground">Read and write</span>
                                    <span className="block text-muted-foreground text-sm">
                                        It can also create, change and delete records as you. Each change is recorded
                                        against your account.
                                    </span>
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                    ) : !access.grantable ? (
                        <Alert
                            type="error"
                            showIcon
                            className="mb-4"
                            message="Cannot authorise from here"
                            description="This app asked for read-only access, which is not available here yet."
                        />
                    ) : (
                        <>
                            <Form.Item<ConsentFormValues> name="scope" hidden>
                                <Input type="hidden" />
                            </Form.Item>
                            <div className="mb-4">
                                <div className="mb-1 font-semibold text-muted-foreground text-xs uppercase">Access</div>
                                {access.initialScope === PERSONAL_ACCESS_TOKEN_SCOPE_FULL ? (
                                    <>
                                        <div className="text-foreground">Read and write</div>
                                        <p className="text-muted-foreground text-sm">
                                            Read-only access is not available here yet.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-foreground">Read only</div>
                                        <p className="text-muted-foreground text-sm">
                                            This app asked for read-only access.
                                        </p>
                                    </>
                                )}
                            </div>
                        </>
                    ))}

                <Form.Item<ConsentFormValues> name="expiryDays" label="Token expires in" rules={[{ required: true }]}>
                    <Select options={expiryOptions.map((o) => ({ value: o.value, label: o.label }))} />
                </Form.Item>

                {adminRules && (
                    <>
                        <Form.Item<ConsentFormValues>
                            name="reason"
                            label={adminRules.reasonRequired ? 'Reason' : 'Reason (optional)'}
                            extra="Incident, ticket or purpose — stored on the token for the audit trail."
                            rules={[
                                ...(adminRules.reasonRequired
                                    ? [{ required: true, whitespace: true, message: 'Give a reason for this token' }]
                                    : []),
                                trimmedReasonRule,
                                {
                                    max: PERSONAL_ACCESS_TOKEN_REASON_MAX_LENGTH,
                                    message: `Reason must be ${PERSONAL_ACCESS_TOKEN_REASON_MAX_LENGTH} characters or fewer`,
                                },
                            ]}
                        >
                            <Input.TextArea rows={2} placeholder="e.g. Authoring the September What's new release" />
                        </Form.Item>

                        <Form.Item<ConsentFormValues>
                            name="restrictToCurrentIp"
                            valuePropName="checked"
                            extra={
                                clientInfo.viewerIp === null
                                    ? 'Your network address could not be determined, so the token cannot be restricted to it.'
                                    : ipRestrictionRequired
                                      ? connectorNetworkAvailable
                                          ? 'Required in this environment unless you allow the Claude connector network below. The token only works from this address, so the MCP client must run on this machine or network.'
                                          : 'Required in this environment: the token only works from this address, so the MCP client must run on this machine or network. Hosted connectors such as claude.ai cannot be used.'
                                      : 'Recommended when the MCP client runs on this machine. Leave it off for hosted connectors such as claude.ai, which call from their own network.'
                            }
                        >
                            <Checkbox
                                disabled={
                                    clientInfo.viewerIp === null ||
                                    (ipRestrictionRequired && !connectorNetworkAvailable)
                                }
                            >
                                Only allow from my current network address
                                {clientInfo.viewerIp ? ` (${clientInfo.viewerIp})` : ''}
                            </Checkbox>
                        </Form.Item>
                        {connectorNetworkAvailable && (
                            <Form.Item<ConsentFormValues>
                                name="allowConnectorNetwork"
                                valuePropName="checked"
                                dependencies={['restrictToCurrentIp']}
                                extra={`Hosted connectors such as claude.ai call from Anthropic's network (${connectorIpAllowlist.join(', ')}), never from your own address. Required when connecting from claude.ai; leave it off for a client running on this machine. This is a shared provider network: the restriction does not identify your Claude account. Anyone with this bearer token and access from that network satisfies the IP restriction.`}
                                rules={[
                                    {
                                        validator: (_rule: unknown, value: boolean | undefined) =>
                                            ipRestrictionSatisfied(ipRestrictionRequired, {
                                                restrictToCurrentIp: form.getFieldValue('restrictToCurrentIp'),
                                                allowConnectorNetwork: value,
                                            })
                                                ? Promise.resolve()
                                                : Promise.reject(
                                                      new Error(
                                                          'Choose at least one network restriction: your current address or the Claude connector network',
                                                      ),
                                                  ),
                                    },
                                ]}
                            >
                                <Checkbox>Allow from the Claude connector network</Checkbox>
                            </Form.Item>
                        )}
                        {ipRestrictionImpossible && (
                            <Alert
                                type="error"
                                showIcon
                                className="mb-4"
                                message="Cannot authorise from here"
                                description="Admin MCP tokens in this environment must be restricted to a network, but your address could not be determined for this request and no Claude connector network is configured."
                            />
                        )}
                    </>
                )}

                <Form.Item<ConsentFormValues>
                    name="tokenName"
                    label="Token name"
                    rules={[
                        { required: true, whitespace: true, message: 'Give this authorisation a memorable name' },
                        { max: 100, message: 'Token name must be 100 characters or fewer' },
                    ]}
                    extra="Shown on the Authorised apps page so you can identify and revoke this token later."
                >
                    <input
                        className="w-full rounded border border-separator bg-card px-3 py-2 text-foreground"
                        maxLength={100}
                    />
                </Form.Item>

                {submitError && (
                    <Alert
                        type="error"
                        showIcon
                        icon={<AlertTriangle className="h-4 w-4" />}
                        message={submitError}
                        className="mb-4"
                    />
                )}

                <div className="flex gap-3">
                    <Button block size="large" onClick={handleDeny} disabled={submitting}>
                        Deny
                    </Button>
                    <Button
                        block
                        size="large"
                        type="primary"
                        htmlType="submit"
                        loading={submitting}
                        disabled={ipRestrictionImpossible || !consentOrg.available || !access.grantable}
                    >
                        Authorise
                    </Button>
                </div>
            </Form>
        </div>
    );
}

function InlineError({ title, message, code }: { title: string; message: string; code?: string }) {
    return (
        <PageController route={Route}>
            <ConsentChrome>
                <div className="text-center">
                    <div className="mb-4 flex justify-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <AlertTriangle className="h-8 w-8 text-error" />
                        </div>
                    </div>
                    {/* curium:allow-heading — OAuth consent error screen, no app shell */}
                    <h1 className="mb-2 font-bold text-foreground text-xl">{title}</h1>
                    <p className="mb-4 text-muted-foreground">{message}</p>
                    {code && (
                        <code className="block break-all rounded bg-muted px-2 py-1 text-muted-foreground text-xs">
                            {code}
                        </code>
                    )}
                </div>
            </ConsentChrome>
        </PageController>
    );
}
